import PageBackTitle from "../Component/PageBackTitle";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import { HiOutlinePlus } from "react-icons/hi";
import { IoSearchOutline } from "react-icons/io5";
import { MdDeleteOutline, MdOutlineModeEditOutline } from "react-icons/md";
import { TbRefresh } from "react-icons/tb";
import API_URL from "../../src/Config";
import "./excavationReviewerConfiguration.css";
import {
  EXCAVATION_PERMIT_LABEL,
  findExcavationPermitType,
} from "./excavationApprovalSetupUtils";

const isEnabled = (value) => value === 1 || value === "1" || value === true;
const formatYesNo = (value) => (isEnabled(value) ? "Yes" : "No");

function ExcavationDepartmentReviewerConfiguration({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const [deletingReviewerId, setDeletingReviewerId] = useState("");
  const containerRef = useRef(null);

  const showSnackbar = useCallback((message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  }, []);

  const fetchReviewerConfiguration = useCallback(async () => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");
      const permitResponse = await axios.get(
        `${API_URL}/api/get_work_permit_types`,
        { headers: { Authorization: authKey } }
      );

      const excavationPermit = findExcavationPermitType(
        permitResponse.data?.work_permit_types || []
      );

      if (!excavationPermit) {
        setRecords([]);
        setFilteredRecords([]);
        setSeverity("error");
        showSnackbar("Excavation permit type was not found.");
        return;
      }

      const setupResponse = await axios.get(
        `${API_URL}/api/permit/departments/setup/${excavationPermit.id}`,
        { headers: { Authorization: authKey } }
      );

      const departments = setupResponse.data?.departments || [];
      const reviewerResponses = await Promise.all(
        departments.map((department) =>
          axios
            .get(
              `${API_URL}/api/permit/departments/reviewers/${department.department_id}`,
              { headers: { Authorization: authKey } }
            )
            .then((response) => ({
              department,
              reviewers: response.data?.reviewers || [],
            }))
        )
      );

      const nextRecords = reviewerResponses.flatMap(
        ({ department, reviewers }) =>
          reviewers.length
            ? reviewers.map((reviewer) => ({
                department_id: department.department_id,
                department_name: department.department_name,
                reviewer_id: reviewer.id || reviewer.reviewer_id || "",
                reviewer_name: reviewer.user_name || "NA",
                reviewer_email: reviewer.user_email || "NA",
                is_primary: reviewer.is_primary,
                can_approve: reviewer.can_approve,
                can_reject: reviewer.can_reject,
                hasReviewer: true,
              }))
            : [
                {
                  department_id: department.department_id,
                  department_name: department.department_name,
                  reviewer_id: "",
                  reviewer_name: "No reviewers configured",
                  reviewer_email: "NA",
                  is_primary: "",
                  can_approve: "",
                  can_reject: "",
                  hasReviewer: false,
                },
              ]
      );

      setRecords(nextRecords);
      setFilteredRecords(nextRecords);
    } catch (error) {
      console.error(
        "Error fetching excavation reviewer configuration:",
        error.message
      );
      setSeverity("error");
      showSnackbar("Unable to fetch reviewer configuration.");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchReviewerConfiguration();
  }, [fetchReviewerConfiguration]);

  useEffect(() => {
    const query = searchInput.trim().toLowerCase();
    if (!query) {
      setFilteredRecords(records);
      return;
    }

    setFilteredRecords(
      records.filter((record) =>
        [
          record.department_name,
          record.reviewer_name,
          record.reviewer_email,
          record.hasReviewer ? formatYesNo(record.is_primary) : "NA",
          record.hasReviewer ? formatYesNo(record.can_approve) : "NA",
          record.hasReviewer ? formatYesNo(record.can_reject) : "NA",
        ]
          .join(" ")
          .toLowerCase()
          .includes(query)
      )
    );
  }, [searchInput, records]);

  const handleRefresh = () => {
    setSearchInput("");
    fetchReviewerConfiguration();
  };

  const handleEditClick = (departmentId) => {
    navigate(`/edit-excavation-reviewer-configuration/${departmentId}`);
  };

  const handleDeleteClick = async (record) => {
    if (!record.reviewer_id) {
      setSeverity("error");
      showSnackbar("Reviewer id was not found.");
      return;
    }

    const confirmed = window.confirm(
      `Remove reviewer ${record.reviewer_name || ""}?`
    );
    if (!confirmed) return;

    try {
      setDeletingReviewerId(record.reviewer_id);
      const authKey = localStorage.getItem("authKey");
      await axios.delete(
        `${API_URL}/api/permit/departments/reviewers/${record.reviewer_id}`,
        { headers: { Authorization: authKey } }
      );
      await fetchReviewerConfiguration();
      setSeverity("success");
      showSnackbar("Reviewer removed successfully");
    } catch (error) {
      console.error("Error removing excavation reviewer:", error.message);
      setSeverity("error");
      showSnackbar(
        error.response?.data?.message ||
          "An error occurred while removing reviewer."
      );
    } finally {
      setDeletingReviewerId("");
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content" ref={containerRef}>
          <div className="data-list-header">
            <div className="data-list-header-content">
              <PageBackTitle>Excavation Reviewer Configuration</PageBackTitle>
              <div className="data-list-heading-content-right-side">
                <div className="search-bar-container">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    className="search-bar"
                  />
                  <IoSearchOutline className="search-bar-icon" />
                </div>

                <Link
                  to="/add-excavation-reviewer-configuration"
                  className="data-list-new"
                >
                  <Tooltip title="New Reviewer Configuration" arrow>
                    <button className="new-button">
                      <HiOutlinePlus className="new-plus-icon" /> New
                    </button>
                  </Tooltip>
                </Link>

                <Tooltip title="Refresh List" arrow>
                  <button className="refresh-button" onClick={handleRefresh}>
                    <TbRefresh className="refresh-icon" />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className="data-list-body">
            <div className="data-list-scroll-body">
              <div className="data-list-table">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader"></div>
                    <div>Loading...</div>
                  </div>
                ) : filteredRecords.length > 0 ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th className="center-alignment">SR. No.</th>
                        <th>PERMIT TYPE</th>
                        <th>DEPARTMENT</th>
                        <th>REVIEWER</th>
                        <th>EMAIL</th>
                        <th className="center-alignment">PRIMARY</th>
                        <th className="center-alignment">CAN APPROVE</th>
                        <th className="center-alignment">CAN REJECT</th>
                        <th className="center-alignment">ACTION</th>
                      </tr>
                    </thead>
                    <tbody className="data-list-scroll-bodys">
                      {filteredRecords.map((record, index) => (
                        <tr
                          key={`${record.department_id}-${
                            record.reviewer_id || index
                          }`}
                        >
                          <td className="center-alignment">{index + 1}</td>
                          <td>{EXCAVATION_PERMIT_LABEL}</td>
                          <td>{record.department_name || "NA"}</td>
                          <td>{record.reviewer_name || "NA"}</td>
                          <td>{record.reviewer_email || "NA"}</td>
                          <td className="center-alignment">
                            {record.hasReviewer
                              ? formatYesNo(record.is_primary)
                              : "NA"}
                          </td>
                          <td className="center-alignment">
                            {record.hasReviewer
                              ? formatYesNo(record.can_approve)
                              : "NA"}
                          </td>
                          <td className="center-alignment">
                            {record.hasReviewer
                              ? formatYesNo(record.can_reject)
                              : "NA"}
                          </td>
                          <td className="edit-pop center-alignment">
                            <div className="excavation-reviewer-list-actions">
                              <button
                                className="button-bg"
                                onClick={() =>
                                  handleEditClick(record.department_id)
                                }
                              >
                                <MdOutlineModeEditOutline /> Edit Reviewers
                              </button>
                              {record.hasReviewer && (
                                <button
                                  className="excavation-reviewer-delete-action"
                                  onClick={() => handleDeleteClick(record)}
                                  disabled={
                                    deletingReviewerId === record.reviewer_id
                                  }
                                >
                                  <MdDeleteOutline />
                                  {deletingReviewerId === record.reviewer_id
                                    ? "Removing..."
                                    : "Remove"}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">
                      No Excavation Reviewer Configuration Found
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default ExcavationDepartmentReviewerConfiguration;
